/**
	* Map view
	* Handles: map container and basic map events including map click search query
	* @module Map
*/

// React & OpenLayers
import React, {useEffect, useRef} from 'react';
import OlMap from 'ol/Map';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import KeyboardPan from 'ol/interaction/KeyboardPan';
import KeyboardZoom from 'ol/interaction/KeyboardZoom';
import { transform } from 'ol/proj';

// Models
import MapToolMode from 'lib/enums/maptoolmode';

// jQuery
import $ from 'jquery';

// Synchronise URL bar to map and vice-versa
import synchronize from 'lib/includes/ol-hashed/main';
import { serialize, deserialize } from 'lib/includes/hashed/hash';

// Tools
import { toPrecision } from 'lib/utilities/maths';
import { 
	replaceHashValues,
	takeScreenshot
} from 'lib/utilities/map';

import 'styles/css/Map.scss';
import 'ol/ol.css';

interface MapProps {
	olMap:OlMap
}

var sync:any;
var mapConfig:any;

const keyboardPan:KeyboardPan = new KeyboardPan();
const keyboardZoom:KeyboardZoom = new KeyboardZoom();

export default function MapContainer({olMap}:MapProps) {
	const mapContainerRef = useRef<HTMLDivElement | null>(null);
	
	mapConfig = olMap.get('opus-config');
	
	let mouseMoved = false;
	let mouseDown = false;
	
	// Initialize map after render of map container complete
	useEffect(() => {
		if(!olMap) 
			return;
		
		global._hash = window.location.hash;
		global._hashed = deserialize(global._hash);
		
		setupMap();
	}, [olMap]);
	
	// Initialize events
	useEffect(() => {
		
		_events.on('tool-mode-changed', (toolMode:MapToolMode) => {
			if(toolMode == MapToolMode.Select)
				autoFocusMap();
		});
		(window as any).replaceHashValues = (obj:any) => replaceHashValues(sync, obj);
		
	}, []);
	
	const onMapClicked = (e:any) => {
		if(mouseMoved) return;
		
		// Handled in views/Tools
		_events.invoke('map-clicked', e);
	}
	const onMapMouseDown = () => {
		mouseMoved = false;
		mouseDown = true;
	}
	const onMapMouseUp = () => {
		mouseDown = false;
	}
	const onMapMouseMove = () => {
		if(mouseDown)
			mouseMoved = true;
	}
	const onMapMouseOver = () => {
		if (!($(':focus').is('input[type="text"], input[type="search"], input[type="password"], input[type="email"], textarea')))
			autoFocusMap();
	}
	const onMapPointerDownCapture = () => {
		if(_config.noFocus)
			return;
		
		const active:any = document.activeElement;
		
		if(active && $(active).is('input[type="text"], input[type="search"], input[type="password"], input[type="email"], textarea'))
			active.blur();
		
		mapContainerRef.current?.focus({ preventScroll: true });
	}
	const autoFocusMap = () => {
		if(!_config.noFocus)
			mapContainerRef.current && $(mapContainerRef.current).focus();
	}
	
	// Convert legacy url hash structure from opus4 to new opus5 structure
	const checkLegacyHash = ():boolean => {
		const store = sync.instance.getStore();
		const hash:string = serialize(store._values);
		
		if(hash.includes('o:') && hash.includes('/:')) {
			handleLegacyFeatureHash();
			return true;
		}
		
		if(!(hash.includes('x:') && hash.includes('y:') && hash.includes('z:')))
			return false;
			
		const parts:string[] = hash.split('/');
		const x:number = Number(parts[1].split(':')[1]);
		const y:number = Number(parts[2].split(':')[1]);
		const z:number = Number(parts[3].split(':')[1]);
		
		if(isNaN(x) || isNaN(y) || isNaN(z))
			return false;
													// OSGB:36		Long/Lat
		const coord:Array<number> = transform([x, y], 'EPSG:27700', 'EPSG:4326');
		const precision:number = Math.max(0, Math.ceil(Math.log(z) / Math.LN2));
		
		const lat:string = toPrecision(coord[1], precision);
		const long:string = toPrecision(coord[0], precision);
		
		/*
			- Remove the legacy values from the hash store
			- Update the values according to what has been converted
			- Then directly modify the location hash
			- Reset the sync entirely
		*/
		
		replaceHashValues(sync, {
			center: `${lat},${long}`,
			zoom: z,
			baselayer: parts[4],
			layers: parts[5]
		});
		
		return true;
	}
	const handleLegacyFeatureHash = () => {
		const store = sync.instance.getStore();
		const hash:string = serialize(store._values);
		
		const parts:string[] = hash.split('/');
		const featureId:number = Number(parts[1].split(':')[1]);
		
		console.log('Feature ID', featureId, hash);
		replaceHashValues(sync, {
			feature: featureId,
			layers: parts[2]
		});
	}
	
	/**
		Setup the map
	*/
	const setupMap = () => {
		// Set the target of the map to the container div
		olMap.setTarget('ol-map-container');
		
		// Add keyboard pan interaction
		olMap.addInteraction(keyboardPan);
		olMap.addInteraction(keyboardZoom);
		
		// Set focus on map
		if(!_config.noFocus)
			mapContainerRef.current.focus();
		
		// Register hashed
		registerHashed();
		
		// Map pointer hand when over certain features
		olMap.on('pointermove', (e:any) => {
			if (e.dragging) return;
			
			const pixel = olMap.getEventPixel(e.originalEvent);
			
			let hit:boolean = false;
			
			olMap.forEachFeatureAtPixel(e.pixel, (feature:Feature<Geometry>) => {
				if(feature.get('existing-site') || feature.get('map-marker') || feature.get('highlight') || feature.get('annotation') === 'streetview')
					hit = true;
			});
			
			olMap.getTargetElement().style.cursor = hit && !_ui.isPointerBusy() ? 'pointer' : '';
		});
		
		// Block right click on map container
		$('#ol-map-container').contextmenu((e:any) => {
			e.preventDefault();
		});
		
		// Take map screenshot after 5 seconds
		_config.screenshotMap && setTimeout(() => takeScreenshot(olMap), 5000);
	}
	
	/**
		Register the hashed module
	*/
	const registerHashed = () => {
		// Unregister existing synchronisation
		sync?.unregister?.();
		
		// Synchronise map with browser's address parameters
		sync = synchronize(olMap, mapConfig);
		sync.registerHashed = registerHashed;
		
		// Check if the url format is old
		checkLegacyHash();
	}
	
	return (
		<div 
			ref={mapContainerRef}
			id='ol-map-container' 
			className='map-container' 
			onClick={onMapClicked}
			onMouseDown={onMapMouseDown}
			onMouseUp={onMapMouseUp}
			onMouseMove={onMapMouseMove}
			onMouseOver={onMapMouseOver}
			onPointerDownCapture={onMapPointerDownCapture}
			tabIndex={-1}
		></div>
	)
}